import React from 'react';
import { motion } from 'framer-motion';
import { 
  ClipboardList, 
  Search, 
  Wrench, 
  CheckCircle2,
  ArrowRight,
  ArrowDown,
  Bell,
  Shield,
  Users
} from 'lucide-react';

const RepairHubSection = () => {
  const workflowSteps = [
    {
      icon: ClipboardList,
      title: 'Intake',
      description: 'Digital check-in with customer details and device info',
      color: 'purple',
    },
    {
      icon: Search,
      title: 'Diagnosis',
      description: 'Document issues and create repair estimates',
      color: 'blue',
    },
    {
      icon: Wrench,
      title: 'Repair',
      description: 'Track work progress and parts used',
      color: 'orange',
    },
    {
      icon: CheckCircle2,
      title: 'Complete',
      description: 'Quality check and customer notification',
      color: 'green',
    },
  ];

  const features = [
    { icon: Bell, text: 'Auto email notifications at each stage' },
    { icon: Shield, text: 'Warranty tracking and management' },
    { icon: Users, text: 'Technician assignment and workload balancing' },
  ];

  const colorClasses = {
    purple: 'bg-primary/20 text-primary border-primary/30',
    blue: 'bg-[#6366F1]/20 text-[#6366F1] border-[#6366F1]/30',
    orange: 'bg-orange-500/20 text-orange-500 border-orange-500/30',
    green: 'bg-[#10B981]/20 text-[#10B981] border-[#10B981]/30',
  };

  return (
    <section id="repair-hub" className="py-16 sm:py-24 md:py-32 relative overflow-hidden" data-testid="repair-hub-section">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/5 to-transparent pointer-events-none" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10 sm:mb-16"
        >
          <span className="text-xs sm:text-sm font-mono tracking-widest uppercase text-primary mb-3 sm:mb-4 block">
            Repair Management
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-4 sm:mb-6">
            Streamlined
            <span className="text-gradient"> Repair Workflow</span>
          </h2>
          <p className="text-base sm:text-lg text-slate-400 max-w-2xl mx-auto px-4">
            From intake to completion, manage every repair with automated tracking, 
            notifications, and a beautiful dashboard.
          </p>
        </motion.div>

        {/* Workflow Steps - Horizontal on desktop, Vertical on mobile */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex flex-col md:flex-row items-center justify-center gap-4 sm:gap-6 md:gap-0 mb-10 sm:mb-16"
          data-testid="workflow-steps"
        >
          {workflowSteps.map((step, index) => (
            <React.Fragment key={step.title}>
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="flex flex-row md:flex-col items-center text-center md:text-center w-full md:w-auto"
                data-testid={`workflow-step-${step.title.toLowerCase()}`}
              >
                <div className={`w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-xl sm:rounded-2xl ${colorClasses[step.color]} flex items-center justify-center mb-0 md:mb-4 mr-4 md:mr-0 border flex-shrink-0`}>
                  <step.icon className="w-7 h-7 sm:w-8 sm:h-8 md:w-10 md:h-10" />
                </div>
                <div className="text-left md:text-center">
                  <h3 className="text-base sm:text-lg font-bold text-white mb-0.5 sm:mb-1">{step.title}</h3>
                  <p className="text-xs sm:text-sm text-slate-400 max-w-[200px] md:max-w-[150px]">{step.description}</p>
                </div>
              </motion.div>
              
              {/* Arrow - horizontal on desktop, vertical on mobile */}
              {index < workflowSteps.length - 1 && (
                <>
                  <div className="hidden md:flex items-center px-4 lg:px-6">
                    <ArrowRight className="w-6 h-6 lg:w-8 lg:h-8 text-primary/50" />
                  </div>
                  <div className="flex md:hidden items-center py-2">
                    <ArrowDown className="w-5 h-5 text-primary/50" />
                  </div>
                </>
              )}
            </React.Fragment>
          ))}
        </motion.div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 gap-6 sm:gap-8 items-center">
          {/* Left - Features List */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-4 sm:space-y-6 order-2 md:order-1"
          >
            <h3 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6">
              Everything you need to run repairs efficiently
            </h3>
            
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex items-start gap-3 sm:gap-4 p-3 sm:p-4 rounded-lg sm:rounded-xl bg-white/5 border border-white/10"
                data-testid={`repair-feature-${index}`}
              >
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <feature.icon className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                </div>
                <span className="text-slate-300 text-sm sm:text-lg">{feature.text}</span>
              </motion.div>
            ))}

            <div className="grid grid-cols-3 gap-2 sm:gap-4 mt-6 sm:mt-8">
              {[
                { value: '50%', label: 'Time Saved' },
                { value: '99%', label: 'On-Time' },
                { value: '4.9', label: 'Rating' },
              ].map((stat, index) => (
                <div key={index} className="text-center p-3 sm:p-4 rounded-lg sm:rounded-xl bg-white/5">
                  <div className="text-lg sm:text-2xl font-bold text-primary">{stat.value}</div>
                  <div className="text-[10px] sm:text-xs text-slate-400">{stat.label}</div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Right - App Screenshot */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative flex justify-center order-1 md:order-2 mb-6 md:mb-0"
          >
            <div className="relative">
              {/* Glow effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-[#10B981]/20 rounded-3xl blur-3xl" />
              
              {/* Phone frame */}
              <div className="relative bg-[#1A1A2E] rounded-[1.5rem] sm:rounded-[2rem] p-2 sm:p-3 border border-white/10 glow-purple">
                <div className="overflow-hidden rounded-[1.25rem] sm:rounded-[1.5rem]">
                  <img
                    src="https://customer-assets.emergentagent.com/job_device-valuation/artifacts/zsgypwwc_IMG_3588_1242x2688.jpeg"
                    alt="Repair Hub Dashboard"
                    className="w-[200px] sm:w-[240px] md:w-[260px] lg:w-[300px] h-auto"
                    data-testid="repair-hub-screenshot"
                  />
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default RepairHubSection;
